/**
 * Templates d'événements narratifs Diplomatie (Lot 6 Phase 3).
 *
 * Chaque template décrit un événement aléatoire pouvant être instancié
 * par `relationEventsSlice.maybeSpawnEvent()` selon des conditions de
 * relations et de contexte. Les messages sont signés par les dirigeants
 * fictifs définis dans `data/competitors.js` (champ `director`).
 *
 * Types d'événements :
 *   1. opa            — OPA hostile (rival ≤ 15)
 *   2. fusion         — Proposition de fusion (allié ≥ 80)
 *   3. plainte        — Plainte concurrence déloyale (ennemi)
 *   4. debauchage     — Tentative de débauchage d'un chauffeur top
 *   5. article_media  — Article presse (rumeur sur concurrent, no-choice)
 *
 * Structure d'un template :
 *   {
 *     id, type, condition: (state, competitor) => bool,
 *     probability, cooldownDays,
 *     build: (state, competitor) => message instance
 *   }
 *
 * 17/06/2026 — Création.
 */

import { readRelationScore } from '../utils/competitorRelations';
import { getLegalFineMultiplier } from '../utils/legalSupport';

/**
 * Helper — relation actuelle entre joueur et un concurrent donné.
 */
const _rel = (state, cid) => readRelationScore(state.competitorRelations, cid);

/** Helper — CA mensuel courant. */
const _monthlyRevenue = (state) => state.monthlyRevenues || 0;

/** Helper — taille de la flotte. */
const _fleetSize = (state) => (state.vehicles || []).length;

/* ─── Helpers de fabrication des messages ─────────────────────────────── */

/**
 * Construit un objet message standard signé par le dirigeant du
 * concurrent (cf. `competitor.director`). Utilisé comme base pour tous
 * les templates.
 */
function buildBaseMessage(competitor, kind, subject, body, choices = []) {
  const director = competitor.director || { name: competitor.name, role: 'Direction' };
  return {
    kind, // 'info' | 'decision'
    senderType: 'competitor',
    senderName: director.name,
    senderRole: director.role,
    senderCompany: competitor.name,
    senderAvatar: competitor.color,
    tone: 'formal',
    subject,
    body,
    choices,
    ttlDays: 12,
    payload: { competitorId: competitor.id },
  };
}

/* ─── Template 1 : OPA hostile ────────────────────────────────────────── */

const TPL_OPA = {
  id: 'narr-opa-hostile',
  type: 'opa',
  cooldownDays: 360, // ≤ 1 par an et par concurrent
  probability: 0.015,
  condition: (state, competitor) =>
    _rel(state, competitor.id) <= 15
    && _monthlyRevenue(state) >= 50000
    && _fleetSize(state) >= 4,
  build: (state, competitor) => {
    const valuation = Math.round((_monthlyRevenue(state) * 12 * 1.4));
    const offerPrice = Math.round(valuation * 0.85); // sous-estimé pour titiller
    // 17/06/2026 (L1) — Juliette atténue le coût de la défense juridique.
    const fineMult = getLegalFineMultiplier(state);
    const defenseCost = Math.round(15000 * fineMult);
    return buildBaseMessage(
      competitor,
      'decision',
      `OFFRE PUBLIQUE D'ACHAT — ${valuation.toLocaleString('fr-FR')} €`,
      `Madame, Monsieur,\n\nAu nom du directoire de **${competitor.name}**, je vous informe que nous lançons formellement une **offre publique d'achat hostile** sur votre société.\n\nNotre proposition : **${offerPrice.toLocaleString('fr-FR')} €** (valorisation à 85 % du juste prix de marché).\n\nVotre position concurrentielle nous gêne. Soit nous nous entendons à l'amiable, soit nous mobilisons nos partenaires financiers et nous forçons la vente.\n\nVous avez 12 jours.\n\n${competitor.director?.name || 'La Direction'}`,
      [
        {
          id: 'mediatize',
          label: 'Médiatiser le scandale',
          tooltip: '+5 réputation, +8 notoriété. Relation -20 avec ce concurrent.',
          effects: [
            { kind: 'reputation', amount: +5 },
            { kind: 'notoriety', amount: +8 },
            { kind: 'competitorRelation', competitorId: competitor.id, amount: -20 },
          ],
        },
        {
          id: 'defend',
          label: fineMult < 1
            ? `Défense juridique (-${defenseCost.toLocaleString('fr-FR')} € · Juliette)`
            : `Défense juridique (-${defenseCost.toLocaleString('fr-FR')} €)`,
          tooltip: 'Cabinet d\'avocats mobilisé. +3 réputation pro. Relation -10.',
          effects: [
            { kind: 'treasury', amount: -defenseCost },
            { kind: 'reputation', amount: +3 },
            { kind: 'competitorRelation', competitorId: competitor.id, amount: -10 },
          ],
        },
        {
          id: 'ignore',
          label: 'Ignorer la lettre',
          tooltip: 'Risque réputationnel. Relation -5.',
          effects: [
            { kind: 'reputation', amount: -3 },
            { kind: 'competitorRelation', competitorId: competitor.id, amount: -5 },
          ],
        },
      ],
    );
  },
};

/* ─── Template 2 : Proposition de fusion ──────────────────────────────── */

const TPL_FUSION = {
  id: 'narr-fusion-proposal',
  type: 'fusion',
  cooldownDays: 540,
  probability: 0.008,
  condition: (state, competitor) =>
    _rel(state, competitor.id) >= 80
    && _monthlyRevenue(state) >= 80000
    && !(state.narrativeFlags || {})[`fusion_${competitor.id}_offered`],
  build: (state, competitor) => buildBaseMessage(
    competitor,
    'decision',
    `Proposition de rapprochement stratégique`,
    `Cher confrère,\n\nNos sociétés se respectent depuis des années. Je vous propose aujourd'hui d'aller plus loin : un **rapprochement capitalistique** entre ${competitor.name} et votre groupe.\n\nNous y gagnerions une couverture nationale unifiée, un pouvoir de négociation décuplé face aux donneurs d'ordre, et des synergies de l'ordre de 8-12 % sur les coûts opérationnels.\n\nLe modèle restera collégial — je ne souhaite ni vous absorber, ni perdre mon indépendance.\n\nQu'en pensez-vous ?\n\nCordialement,\n${competitor.director?.name || 'La Direction'}`,
    [
      {
        id: 'accept',
        label: 'Accepter la fusion (prestige)',
        tooltip: '+10 réputation, +15 notoriété, flag fusion. Effet narratif fort.',
        effects: [
          { kind: 'reputation', amount: +10 },
          { kind: 'notoriety', amount: +15 },
          { kind: 'competitorRelation', competitorId: competitor.id, amount: +5 },
          { kind: 'flag', flag: `fusion_${competitor.id}_accepted`, value: true },
        ],
      },
      {
        id: 'negotiate',
        label: 'Négocier (partenariat sans fusion)',
        tooltip: 'Sage : on garde son autonomie. Relation +3.',
        effects: [
          { kind: 'competitorRelation', competitorId: competitor.id, amount: +3 },
          { kind: 'flag', flag: `fusion_${competitor.id}_offered`, value: true },
        ],
      },
      {
        id: 'refuse',
        label: 'Refuser poliment',
        tooltip: 'Relation -5. Vous restez votre propre patron.',
        effects: [
          { kind: 'competitorRelation', competitorId: competitor.id, amount: -5 },
          { kind: 'flag', flag: `fusion_${competitor.id}_offered`, value: true },
        ],
      },
    ],
  ),
};

/* ─── Template 3 : Plainte concurrence déloyale ───────────────────────── */

const TPL_PLAINTE = {
  id: 'narr-plainte-concurrence',
  type: 'plainte',
  cooldownDays: 240,
  probability: 0.02,
  condition: (state, competitor) =>
    _rel(state, competitor.id) <= 25
    && _fleetSize(state) >= 6,
  build: (state, competitor) => {
    // 17/06/2026 (L1) — Juliette atténue les montants : -30 % sur amende
    // amiable + amende ignorée. La défense juridique reste pleine
    // (frais d'avocat fixes), mais sécurise un gain de réputation accru.
    const fineMult = getLegalFineMultiplier(state);
    const amicableCost = Math.round(8000 * fineMult);
    const ignoreCost = Math.round(18000 * fineMult);
    return buildBaseMessage(
    competitor,
    'decision',
    `Mise en demeure — Pratiques anti-concurrentielles`,
    `Madame, Monsieur,\n\nPar la présente, et après constatation par huissier, ${competitor.name} engage une **procédure pour pratiques anti-concurrentielles** à votre encontre.\n\nMotifs invoqués :\n• Tarification prédatrice sur des appels d'offres récents\n• Démarchage agressif de nos clients historiques\n• Concurrence déloyale au sens de l'article L.420-2 du Code de commerce\n\nNotre conseil juridique vous notifie une **mise en demeure** sous 30 jours. À défaut d'accord amiable, l'affaire sera portée devant le **Tribunal de commerce de Paris**.\n\nNous restons ouverts à un règlement extrajudiciaire.\n\n${competitor.director?.name || 'La Direction'}\n*Sous le sceau de Me. Hofmann, avocat au Barreau de Paris.*`,
    [
      {
        id: 'amicable',
        label: fineMult < 1
          ? `Règlement amiable (-${amicableCost.toLocaleString('fr-FR')} € · Juliette négocie)`
          : `Règlement amiable (-${amicableCost.toLocaleString('fr-FR')} €)`,
        tooltip: 'Dossier classé. Relation +2.',
        effects: [
          { kind: 'treasury', amount: -amicableCost },
          { kind: 'competitorRelation', competitorId: competitor.id, amount: +2 },
        ],
      },
      {
        id: 'defend',
        label: 'Défense juridique (-12 000 €, gain incertain)',
        tooltip: '50 % de chances de gagner. Si gain : +5 réputation. Relation -3.',
        effects: [
          { kind: 'treasury', amount: -12000 },
          // L'issue probabiliste est gérée par messageSlice; pour le MVP
          // Phase 3 on simplifie en effet certain (le coût justice est payé,
          // pas d'amende additionnelle). Le random sera affiné en L1 avec
          // la fonction support juridique.
          { kind: 'reputation', amount: +3 },
          { kind: 'competitorRelation', competitorId: competitor.id, amount: -3 },
        ],
      },
      {
        id: 'ignore',
        label: `Ignorer (-${ignoreCost.toLocaleString('fr-FR')} € au tribunal)`,
        tooltip: 'Amende automatique. -5 réputation.',
        effects: [
          { kind: 'treasury', amount: -ignoreCost },
          { kind: 'reputation', amount: -5 },
          { kind: 'competitorRelation', competitorId: competitor.id, amount: -8 },
        ],
      },
    ],
  );
  },
};

/* ─── Template 4 : Débauchage d'un chauffeur top ──────────────────────── */

const TPL_DEBAUCHAGE = {
  id: 'narr-debauchage-driver',
  type: 'debauchage',
  cooldownDays: 180,
  probability: 0.025,
  condition: (state, competitor) => {
    if (_rel(state, competitor.id) > 35) return false;
    // Exige un chauffeur senior (level ≥ 4) en poste.
    const topDriver = (state.drivers || []).find(d => (d.level || 1) >= 4 && d.status !== 'fired');
    return !!topDriver;
  },
  build: (state, competitor) => {
    const topDriver = (state.drivers || []).find(d => (d.level || 1) >= 4 && d.status !== 'fired');
    const proposedRaise = Math.round((topDriver?.salaryGross || 2500) * 0.15);
    return {
      ...buildBaseMessage(
        competitor,
        'decision',
        `Tentative de débauchage — ${topDriver?.name || 'votre top chauffeur'}`,
        `Bonjour,\n\nVeuillez accepter mes excuses pour la franchise du message. ${competitor.name} a contacté **${topDriver?.name || 'un de vos meilleurs chauffeurs'}** avec une proposition salariale **+${proposedRaise.toLocaleString('fr-FR')} €/mois** + primes export.\n\nIl/Elle nous a parlé. Honnêteté oblige : la balle est dans votre camp.\n\n— ${competitor.director?.name || 'La Direction'}`,
        [
          {
            id: 'counter',
            label: `Surenchère (+${proposedRaise.toLocaleString('fr-FR')} €/mois)`,
            tooltip: `Augmentation appliquée au chauffeur. Relation -12 avec ce concurrent.`,
            effects: [
              { kind: 'driverRaise', amount: proposedRaise },
              { kind: 'driverSatisfaction', amount: +12 },
              { kind: 'competitorRelation', competitorId: competitor.id, amount: -12 },
            ],
          },
          {
            id: 'let_go',
            label: 'Laisser partir',
            tooltip: `${topDriver?.name || 'Le chauffeur'} quitte la société. -3 réputation, -8 relation.`,
            effects: [
              { kind: 'driverFire' },
              { kind: 'reputation', amount: -3 },
              { kind: 'competitorRelation', competitorId: competitor.id, amount: -8 },
            ],
          },
        ],
      ),
      driverId: topDriver?.id || null,
    };
  },
};

/* ─── Template 5 : Article presse (no-choice, effet passif) ───────────── */

const TPL_ARTICLE = {
  id: 'narr-press-article',
  type: 'article_media',
  cooldownDays: 90,
  probability: 0.015,
  condition: () => true,
  build: (state, competitor) => {
    const rel = _rel(state, competitor.id);
    const positive = rel >= 60;
    const negative = rel <= 30;
    const tone = positive ? 'éloge' : negative ? 'critique' : 'neutre';
    return buildBaseMessage(
      competitor,
      'info',
      positive
        ? `« ${competitor.name} » fait la Une — Article élogieux`
        : negative
          ? `« ${competitor.name} » sous le feu de la presse`
          : `Article presse sur « ${competitor.name} »`,
      positive
        ? `**L'Officiel Transports & Logistique** consacre 2 pages à votre principal allié ${competitor.name}.\n\n« Une montée en puissance maîtrisée, des choix RH inspirés et une stratégie claire sur le marché ${competitor.specialties?.[0] || 'transport'}. »\n\nVotre proximité avec eux rejaillit positivement sur votre image.\n\n*— Effet : votre réputation gagne 1 point par contagion d'image.*`
        : negative
          ? `**Le Canard Logistique** publie une enquête à charge sur ${competitor.name} et ses pratiques.\n\nL'article cite « des manquements répétés à la convention collective transport » et « une stratégie tarifaire opportuniste ».\n\nLes lecteurs avertis distinguent votre société de la leur — votre image gagne marginalement.\n\n*— Effet : votre réputation gagne 1 point par contraste.*`
          : `Article presse standard mentionnant ${competitor.name}. Ton ${tone}. Pas d'impact significatif sur votre image.`,
      [],
    );
  },
};

/**
 * Liste exportée des templates dans l'ordre de priorité d'évaluation.
 * Le slice itère cette liste et instancie le premier template dont la
 * condition + cooldown + probabilité passent.
 */
export const RELATION_EVENT_TEMPLATES = [
  TPL_OPA,
  TPL_FUSION,
  TPL_PLAINTE,
  TPL_DEBAUCHAGE,
  TPL_ARTICLE,
];

/**
 * Helper exposé pour les tests : récupère un template par id.
 */
export function getRelationEventTemplate(id) {
  return RELATION_EVENT_TEMPLATES.find(t => t.id === id) || null;
}
